/**
 * GlobalSEOSignals — Direct DOM injection for global SEO signals.
 * Replaces react-helmet-async Helmet with useEffect + document manipulation.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContextNew';
import { SITE_URL } from '@/lib/seo';
import { setMetaName, setMetaHttpEquiv, setLink, setLinksGroup } from '@/lib/seo-dom';

// Regional hreflang variants removed — now handled exclusively by SEOHead.tsx

const DNS_PREFETCH = [
  'https://www.googletagmanager.com',
  'https://www.google-analytics.com',
  'https://region1.google-analytics.com',
  'https://www.facebook.com',
  'https://connect.facebook.net',
  'https://px.ads.linkedin.com',
];

const ROUTE_HINTS: Record<string, string[]> = {
  '/contact': ['https://api.web3forms.com'],
};

export function GlobalSEOSignals() {
  const { language } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    // Stable freshness — no fake dynamic dates (Google penalizes fake freshness)
    const lastModified = '2026-03-22';
    const revisitAfter = '7 days';

    // AI crawler directives are handled by AdvancedCrawlDirectives — no duplication here

    // Content freshness (stable dates only)
    setMetaName('revised', lastModified);
    setMetaName('date', lastModified);
    setMetaHttpEquiv('last-modified', lastModified);
    setMetaName('revisit-after', revisitAfter);
    setMetaName('content-language', language);

    // Sitemap reference
    setLink('sitemap', `${SITE_URL}/sitemap.xml`, { type: 'application/xml' });

    // RSS feed for blog
    setLink('alternate-rss', `${SITE_URL}/rss.xml`, { type: 'application/rss+xml', title: 'Call IT Dev Blog' });

    // DNS prefetch (including fonts & CDN)
    setLinksGroup('dns-prefetch', [
      ...DNS_PREFETCH,
      'https://fonts.googleapis.com',
      'https://fonts.gstatic.com',
      'https://cdnjs.cloudflare.com',
    ].map(origin => ({
      rel: 'dns-prefetch',
      href: origin,
    })));

    // Route-specific preconnects
    const routeKey = Object.keys(ROUTE_HINTS).find(k => location.pathname.includes(k));
    const routePreconnects = routeKey ? ROUTE_HINTS[routeKey] : [];
    setLinksGroup('route-preconnect', routePreconnects.map(origin => ({
      rel: 'preconnect',
      href: origin,
      attrs: { crossorigin: 'anonymous' },
    })));

    // Hreflang is now handled exclusively by SEOHead.tsx — removed from here to prevent duplicates

  }, [language, location.pathname]);

  return null;
}
